import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AircraftDefect,
  AircraftCruiseRow,
  AircraftDocument,
  AircraftMaintenance,
  AircraftMaintenanceEvent,
  AircraftPowerSetting,
  AircraftPerformance,
  AircraftSpeed,
  EmailPreference,
  LoginMethod,
  LoginSession,
  MemberBanner,
  OptionalEmailCategory,
  OwnLoginEvent,
  SignInSettings,
} from '@jupi/shared'
import { ApiError, api, apiFetch, type AircraftDents } from '@jupi/ui'

// One module for every server interaction the member app makes. Query keys
// are structural, so a mutation can invalidate exactly the range it
// affected rather than blowing the whole cache away.

export interface Aircraft {
  registration: string
  aircraftType: string | null
  pictureUpdatedAt: string | null
  seats: number | null
  serviceCeiling: number | null
  maxRange: number | null
  mtow: number | null
  engineType: string | null
  fuelConsumptionUsg: number | null
  fuelType: string
  comment: string | null
  groupCode: string | null
  clubOwned: boolean
  available: boolean
  custodianUserId: string | null
  highSeasonApply: boolean
  highSeasonStart: string | null
  highSeasonEnd: string | null
  highSeasonDays: number
  lowSeasonDays: number
  highSeasonAdvance: number
  lowSeasonAdvance: number
  approvedFuels: string | null
  stc: string | null
  afmRevision: string | null
  checklistRevision: string | null
  equipment: string | null
  transponderMode: string | null
  pbn: string[]
  hasGps: boolean
  hasFuelCardPin: boolean
  // From the flight log; null until the aircraft has an open journey log.
  totals: { airframeMinutes: number; landings: number } | null
  // Fuel on board after the last logged flight; null with no fuel history.
  fuel: { litres: number; usableLitres: number | null; estimated: boolean; uncertain: boolean } | null
  hasPoh: boolean
  hasChecklist: boolean
}

// A row of GET /api/aircraft/:registration/flights: no pilot, by design.
export interface AircraftFlight {
  flightId: string
  takeoffAt: string
  departure: string
  arrival: string
  flightMinutes: number
  landings: number
}

export interface Booking {
  bookingId: number
  registration: string
  aircraftType: string | null
  userId: string
  displayName: string
  phone: string | null
  createdBy: string | null
  createdByName: string | null
  kind: 'reservation' | 'option'
  status: 'active' | 'cancelled' | 'promoted'
  startsAt: string
  endsAt: string
  estimatedFlightHours: number
  comment: string | null
  isMaintenance: boolean
  bookingType: 'private' | 'training' | 'maintenance'
  bulkGroupId: string | null
  isFirePatrol: boolean
  cancelledAt: string | null
  cancelReason: string | null
  createdAt: string
  updatedAt: string
  // A standby's drop time, null if it has none (and on a reservation).
  expiresAt: string | null
  // A standby's place in line (1 = first); null on anything else.
  queuePosition: number | null
  // A flight school lesson (flight.bookings); null on a plain booking.
  instructorUserId: string | null
  instructorName: string | null
  enrolmentId: number | null
  courseCode: string | null
  lessonId: number | null
  lessonNumber: string | null
  lessonTitle: string | null
}

export interface MaintenanceStatus {
  registration: string
  aircraftType: string | null
  coefficient: number
  maintWindowStart: string | null
  maintWindowEnd: string | null
  hoursAtBaseline: number
  hoursNow: number
  nextMaintenanceHours: number | null
  nextMaintenanceDescription: string | null
  hoursRemaining: number | null
  outcome: { kind: 'date'; date: string } | { kind: 'exceeded' } | { kind: 'no_estimate' }
}

export interface AircraftMessage {
  messageId: number
  registration: string
  messageClass: number
  message: string
  airframeHours: number | null
  nextMaintenanceHours: number | null
  nextMaintenanceDesc: string | null
  expiresOn: string | null
  status: string
  submittedByName: string | null
  submittedAt: string
  acknowledgedAt: string | null
}

export interface Member {
  userId: string
  displayName: string
  nickname?: string | null
  firstName: string | null
  lastName: string | null
  memberNumber: string | null
  detailsHidden: boolean
  avatarStyle: 'initials' | 'avataaars' | 'bottts'
  avatarVersion: number | null
  email?: string
  phone?: string
  address?: string
  postalCode?: string
  city?: string
  // Only present when viewing your own profile, or as an admin -- see
  // GET /api/members/:id.
  iceContactName?: string | null
  iceContactPhone?: string | null
  licenceNumber?: string | null
  // When the member turned the booking licence warning off (#225).
  licenceNoticeOffAt?: string | null
  dateOfBirth?: string | null
  memberSince?: string | null
  canMakeReservations?: boolean
  // From the /join application, copied on approval; null for members who
  // did not join through the form.
  membershipType?: 'full' | 'junior' | 'supporting' | null
  licenceLevel?: string | null
  licenceRatings?: string[]
  licenceExpiredYear?: number | null
  trainingDetails?: string | null
  flightHours?: number | null
  additionalInfo?: string | null
}

export const queryKeys = {
  aircraft: ['aircraft'] as const,
  bookings: (params: Record<string, unknown> = {}) => ['bookings', params] as const,
  myBookings: ['bookings', 'mine'] as const,
  maintenance: ['maintenance', 'status'] as const,
  maintenanceSchedules: ['maintenance', 'schedules'] as const,
  messages: (registration?: string) => ['messages', registration ?? 'all'] as const,
  members: ['members'] as const,
  aircraftPerformance: (registration: string) => ['aircraft', registration, 'performance'] as const,
  sessions: ['auth', 'sessions'] as const,
  logins: ['auth', 'logins'] as const,
}

export function useAircraft() {
  return useQuery({
    queryKey: queryKeys.aircraft,
    queryFn: () => api.get<{ aircraft: Aircraft[] }>('/api/aircraft').then((r) => r.aircraft),
    // The fleet changes a few times a decade.
    staleTime: 10 * 60_000,
  })
}

export function useAircraftDocuments(registration: string | undefined) {
  return useQuery({
    queryKey: ['aircraft', registration ?? '', 'documents'] as const,
    enabled: Boolean(registration),
    queryFn: () =>
      api.get<{ documents: AircraftDocument[] }>(`/api/aircraft/${registration}/documents`).then((r) => r.documents),
    staleTime: 60_000,
  })
}

// The open maintenance events of the whole fleet: the grey "in maintenance"
// status on the fleet cards and aircraft pages.
export function useMaintenanceEvents() {
  return useQuery({
    queryKey: ['aircraft-maintenance-events'] as const,
    queryFn: () =>
      api.get<{ events: AircraftMaintenanceEvent[] }>('/api/aircraft-maintenance-events').then((r) => r.events),
    staleTime: 60_000,
  })
}

export function useAircraftPowerSettings(registration: string | undefined) {
  return useQuery({
    queryKey: ['aircraft', registration ?? '', 'power-settings'] as const,
    enabled: Boolean(registration),
    queryFn: () =>
      api.get<{ settings: AircraftPowerSetting[] }>(`/api/aircraft/${registration}/power-settings`).then((r) => r.settings),
    staleTime: 10 * 60_000,
  })
}

export function useAircraftFlightManual(registration: string | undefined) {
  return useQuery({
    queryKey: ['aircraft', registration ?? '', 'flight-manual'] as const,
    enabled: Boolean(registration),
    queryFn: () => api.get<{ speeds: AircraftSpeed[]; cruise: AircraftCruiseRow[] }>(`/api/aircraft/${registration}/flight-manual`),
    staleTime: 10 * 60_000,
  })
}

// Fetched when a pilot asks for it, never cached: the PIN does not sit in
// the query cache for the next person on the phone to find.
export const fetchFuelCardPin = (registration: string) =>
  api.get<{ pin: string }>(`/api/aircraft/${registration}/fuel-card-pin`).then((r) => r.pin)

export function useAircraftFlights(registration: string | undefined) {
  return useQuery({
    queryKey: ['aircraft', registration ?? '', 'flights'] as const,
    enabled: Boolean(registration),
    queryFn: () => api.get<{ flights: AircraftFlight[] }>(`/api/aircraft/${registration}/flights`).then((r) => r.flights),
    staleTime: 60_000,
  })
}

// Performance and W&B data. null, not an error, when none has been entered:
// most of the fleet page does not need it.
export function useAircraftPerformance(registration: string | undefined) {
  return useQuery({
    queryKey: queryKeys.aircraftPerformance(registration ?? ''),
    enabled: Boolean(registration),
    queryFn: () =>
      api
        .get<{ performance: AircraftPerformance }>(`/api/aircraft/${registration}/performance`)
        .then((r) => r.performance)
        .catch((error: unknown) => {
          if (error instanceof ApiError && error.status === 404) return null
          throw error
        }),
    staleTime: 10 * 60_000,
  })
}

export function useBookings(params: { from?: Date; to?: Date; registration?: string } = {}) {
  const search = new URLSearchParams()
  if (params.from) search.set('from', params.from.toISOString())
  if (params.to) search.set('to', params.to.toISOString())
  if (params.registration) search.set('registration', String(params.registration))
  const qs = search.toString()

  return useQuery({
    queryKey: queryKeys.bookings({ ...params, from: params.from?.toISOString(), to: params.to?.toISOString() }),
    queryFn: () =>
      api.get<{ bookings: Booking[] }>(`/api/bookings${qs ? `?${qs}` : ''}`).then((r) => r.bookings),
  })
}

export function useMyBookings() {
  return useQuery({
    queryKey: queryKeys.myBookings,
    queryFn: () => api.get<{ bookings: Booking[] }>('/api/bookings/mine').then((r) => r.bookings),
  })
}

export function useMaintenanceStatus() {
  return useQuery({
    queryKey: queryKeys.maintenance,
    queryFn: () =>
      api.get<{ status: MaintenanceStatus[] }>('/api/maintenance/status').then((r) => r.status),
  })
}

// Each aircraft's next maintenance and the hours left before it, against
// the flight log total: the Fleet page's bar.
export function useMaintenanceSchedules(enabled = true) {
  return useQuery({
    queryKey: queryKeys.maintenanceSchedules,
    queryFn: () =>
      api.get<{ aircraft: AircraftMaintenance[] }>('/api/maintenance/schedules').then((r) => r.aircraft),
    enabled,
  })
}

export function useAircraftMessages(registration?: string) {
  return useQuery({
    queryKey: queryKeys.messages(registration),
    queryFn: () =>
      api
        .get<{ messages: AircraftMessage[] }>(`/api/aircraft-messages${registration ? `?registration=${registration}` : ''}`)
        .then((r) => r.messages),
  })
}

export function useMember(userId: string | undefined) {
  return useQuery({
    queryKey: ['member', userId],
    queryFn: () => api.get<{ member: Member }>(`/api/members/${userId}`).then((r) => r.member),
    enabled: Boolean(userId),
  })
}

// Anything that changes a booking invalidates every booking list and the
// maintenance forecast -- the forecast reads flight-time estimates off
// bookings, so a new one moves the predicted maintenance date.
function useBookingInvalidation() {
  const queryClient = useQueryClient()
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['bookings'] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance }),
    ])
  }
}

export interface BookingFormValues {
  registration: string
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  estimatedFlightHours: string | number
  comment?: string | null
  kind?: 'reservation' | 'option'
  bookingType?: 'private' | 'training' | 'maintenance'
  dropHoursBeforeStart?: number | null
  userId?: string
  enrolmentId?: number | null
  lessonId?: number | null
  instructorUserId?: string | null
}

export function useCreateBooking() {
  const invalidate = useBookingInvalidation()
  return useMutation({
    mutationFn: (values: BookingFormValues) =>
      api.post<{ booking: Booking }>('/api/bookings', values).then((r) => r.booking),
    onSuccess: invalidate,
  })
}

export function useUpdateBooking() {
  const invalidate = useBookingInvalidation()
  return useMutation({
    mutationFn: ({ bookingId, ...values }: BookingFormValues & { bookingId: number }) =>
      api.patch<{ booking: Booking }>(`/api/bookings/${bookingId}`, values).then((r) => r.booking),
    onSuccess: invalidate,
  })
}

export interface TransferTarget {
  userId: string
  displayName: string
}

// Names only: who a reservation can be handed to.
export function useTransferTargets(enabled: boolean) {
  return useQuery({
    queryKey: ['bookings', 'transfer-targets'],
    queryFn: () => api.get<{ members: TransferTarget[] }>('/api/bookings/transfer-targets').then((r) => r.members),
    enabled,
  })
}

export function useTransferBooking() {
  const invalidate = useBookingInvalidation()
  return useMutation({
    mutationFn: ({ bookingId, toUserId }: { bookingId: number; toUserId: string }) =>
      api.post<{ booking: Booking }>(`/api/bookings/${bookingId}/transfer`, { toUserId }).then((r) => r.booking),
    onSuccess: invalidate,
  })
}

export interface CancelResult {
  cancelled: number
  promoted: { bookingId: number; userId: string }[]
}

export function useCancelBooking() {
  const invalidate = useBookingInvalidation()
  return useMutation({
    mutationFn: ({ bookingId, reason }: { bookingId: number; reason?: string }) =>
      api.delete<CancelResult>(`/api/bookings/${bookingId}`, { reason }),
    onSuccess: invalidate,
  })
}

// The public list (published defects and hold items), plus the caller's own
// pending reports, so a reporter sees theirs straight away.
export function useAircraftDefects(registration?: string) {
  const suffix = registration ? `&registration=${registration}` : ''
  return useQuery({
    queryKey: ['defects', registration ?? 'all'],
    queryFn: async () => {
      const [published, mine] = await Promise.all([
        api.get<{ defects: AircraftDefect[] }>(`/api/aircraft-defects?${suffix.slice(1)}`),
        api.get<{ defects: AircraftDefect[] }>(`/api/aircraft-defects?mine=true${suffix}`),
      ])
      return {
        // Published defects and hold items, which pilots see too.
        published: published.defects,
        mine: mine.defects.filter((d) => d.status === 'pending'),
      }
    },
  })
}

export function useReportDefect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { registration: string; message: string; photo: File | null }) => {
      const body = new FormData()
      body.set('registration', input.registration)
      body.set('message', input.message)
      if (input.photo) body.set('file', input.photo)
      // FormData, so not api.post (which JSON-encodes its body).
      return apiFetch<{ defect: AircraftDefect }>('/api/aircraft-defects', { method: 'POST', body })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['defects'] }),
  })
}

// --- Your sessions ---------------------------------------------------------

export function useSessions() {
  return useQuery({
    queryKey: queryKeys.sessions,
    queryFn: () => api.get<{ sessions: LoginSession[] }>('/api/auth/sessions').then((r) => r.sessions),
  })
}

export function useOwnLogins() {
  return useQuery({
    queryKey: queryKeys.logins,
    queryFn: () => api.get<{ events: OwnLoginEvent[] }>('/api/auth/logins').then((r) => r.events),
  })
}

// Ending a session writes a history row too, so both lists reload.
function useSessionMutation<T>(mutationFn: (input: T) => Promise<unknown>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
        queryClient.invalidateQueries({ queryKey: queryKeys.logins }),
      ]),
  })
}

export const useRevokeSession = () => useSessionMutation((id: number) => api.delete(`/api/auth/sessions/${id}`))

export const useRevokeOtherSessions = () =>
  useSessionMutation(() => api.post<{ revoked: number }>('/api/auth/sessions/revoke-others', {}))

// --- Email preferences -----------------------------------------------------

const emailPreferencesKey = ['me', 'email-preferences'] as const

export function useEmailPreferences() {
  return useQuery({
    queryKey: emailPreferencesKey,
    queryFn: () =>
      api.get<{ preferences: EmailPreference[] }>('/api/auth/me/email-preferences').then((r) => r.preferences),
  })
}

export function useSetEmailPreference() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { category: OptionalEmailCategory; enabled: boolean }) =>
      api.put<{ preferences: EmailPreference[] }>('/api/auth/me/email-preferences', input),
    onSuccess: ({ preferences }) => queryClient.setQueryData(emailPreferencesKey, preferences),
  })
}

// --- Sign-in method, password and passkeys ---------------------------------

const signInKey = ['me', 'sign-in'] as const

export function useSignInSettings() {
  return useQuery({ queryKey: signInKey, queryFn: () => api.get<SignInSettings>('/api/auth/sign-in') })
}

// Every sign-in change answers with the new settings, and writes a line to
// the member's own sign-in history.
function useSignInMutation<T>(mutationFn: (input: T) => Promise<SignInSettings>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: async (settings) => {
      queryClient.setQueryData(signInKey, settings)
      await queryClient.invalidateQueries({ queryKey: queryKeys.logins })
    },
  })
}

export const useSetLoginMethod = () =>
  useSignInMutation((method: LoginMethod) => api.put<SignInSettings>('/api/auth/sign-in/method', { method }))

export const useRenamePasskey = () =>
  useSignInMutation(({ id, name }: { id: string; name: string }) =>
    api.patch<SignInSettings>(`/api/auth/passkeys/${encodeURIComponent(id)}`, { name }),
  )

export const useRemovePasskey = () =>
  useSignInMutation((id: string) => api.delete<SignInSettings>(`/api/auth/passkeys/${encodeURIComponent(id)}`))

// Registration is a browser ceremony between two calls; the card runs it
// and hands the result here.
export const useRegisterPasskey = () =>
  useSignInMutation((input: { response: unknown; name?: string }) =>
    api.post<SignInSettings>('/api/auth/passkeys/register/verify', input),
  )

export function useChangePassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) => api.post('/api/auth/password/change', input),
    // The other devices were just signed out.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
        queryClient.invalidateQueries({ queryKey: queryKeys.logins }),
      ]),
  })
}

export const useSendPasswordSetupLink = () => useMutation({ mutationFn: () => api.post('/api/auth/password/setup-link', {}) })

// --- Member banner ----------------------------------------------------------

// The announcement under the header bar, or null while it is off. Polled
// slowly so a switch-off reaches a tab that has been open all day.
export function useMemberBanner() {
  return useQuery({
    queryKey: ['banner'] as const,
    queryFn: () => api.get<{ banner: MemberBanner | null }>('/api/banner').then((r) => r.banner),
    refetchInterval: 5 * 60_000,
  })
}

// The dents on one aircraft, in the view its type resolves to. The card
// shows nothing when `view` is null or `dents` is empty.
export function useAircraftDents(registration: string | undefined) {
  return useQuery({
    queryKey: ['aircraft', registration, 'dents'],
    enabled: Boolean(registration),
    queryFn: () => api.get<AircraftDents>(`/api/aircraft-dents?registration=${registration}`),
  })
}
